import { type Component, createEffect, createResource, createSignal, Show } from 'solid-js'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Switch from '~/components/Switch'
import { useToast } from '~/components/useToast'
import { auditService } from '~/services/audit.service'
import { resolveError } from '~/utils/error'

interface TabAuditoriaProps {
  companyId: string
}

const TabAuditoria: Component<TabAuditoriaProps> = props => {
  const toast = useToast()
  const [enabled, setEnabled] = createSignal(true)
  const [clickhouseUrl, setClickhouseUrl] = createSignal('')
  const [clickhouseUser, setClickhouseUser] = createSignal('')
  const [clickhousePassword, setClickhousePassword] = createSignal('')
  const [clickhouseDb, setClickhouseDb] = createSignal('audit')
  const [saving, setSaving] = createSignal(false)

  const [config, { refetch }] = createResource(
    () => props.companyId,
    async companyId => {
      try {
        return await auditService.getConfig('company', companyId)
      } catch {
        return null
      }
    }
  )

  createEffect(() => {
    const c = config()
    if (c) {
      setEnabled(c.visible ?? c.captureEnabled ?? true)
      if (c.companyEndpointConfigured) {
        setClickhouseUrl(c.clickhouseUrl ?? '')
        setClickhouseUser(c.clickhouseUser ?? '')
        setClickhouseDb(c.clickhouseDb ?? 'audit')
      } else {
        setClickhouseUrl('')
        setClickhouseUser('')
        setClickhouseDb('audit')
      }
      setClickhousePassword('')
    }
  })

  const handleSave = async () => {
    setSaving(true)
    try {
      const url = clickhouseUrl().trim()
      const payload: Record<string, unknown> = {
        scope: 'company',
        companyId: props.companyId,
        enabled: enabled(),
        clickhouseUrl: url || null,
        clickhouseUser: clickhouseUser().trim() || null,
        clickhouseDb: clickhouseDb().trim() || 'audit'
      }
      const pwd = clickhousePassword().trim()
      if (pwd) payload.clickhousePassword = pwd
      // Si url queda vacía, limpiamos credenciales para desactivar fan-out.
      if (!url) {
        payload.clickhouseUser = null
        payload.clickhouseDb = null
        if (!pwd) delete payload.clickhousePassword
      }
      await auditService.saveConfig(payload as never)
      toast.addToast({ title: 'Auditoría actualizada', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const effectiveHint = () => {
    const c = config()
    if (!c) return ''
    return `${c.clickhouseUrl ?? '—'} / DB ${c.clickhouseDb}`
  }

  return (
    <div class="space-y-4">
      <Show when={config.loading}>
        <p class="text-sm text-foreground-subtle">Cargando auditoría...</p>
      </Show>

      <Show when={!config.loading}>
        <Card>
          <Card.Title>
            <div class="flex items-center gap-2">
              <Icon icon="material-symbols-light:database-outline" size={20} />
              <h3 class="text-base font-semibold">Auditoría</h3>
            </div>
          </Card.Title>
          <Card.Body>
            <div class="space-y-4">
              <Switch
                label="Auditoría habilitada para esta empresa"
                helperText="Si se desactiva, la empresa no ve su auditoría en el panel (pero se sigue guardando en el sistema para el root)."
                checked={enabled()}
                onChange={e => setEnabled(e.currentTarget.checked)}
              />

              <Alert variant="info">
                Los eventos se guardan <strong>siempre</strong> en el ClickHouse del sistema. Si configuras un endpoint propio, se guardan <strong>además</strong> en el tuyo (fan-out). Vacío = solo
                sistema.
              </Alert>

              <div class="rounded-lg border border-border p-4 space-y-4">
                <h4 class="text-sm font-semibold flex items-center gap-2">
                  <Icon icon="material-symbols-light:database-outline" size={18} />
                  ClickHouse de la empresa (opcional, fan-out)
                </h4>

                <Input
                  label="URL de ClickHouse"
                  placeholder="http://clickhouse.empresa.com:8123"
                  helperText="Vacío = solo se usa el del sistema. Rellena solo si la empresa tiene su propio ClickHouse."
                  value={clickhouseUrl()}
                  onInput={e => setClickhouseUrl(e.currentTarget.value)}
                />

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Usuario" placeholder="default" value={clickhouseUser()} onInput={e => setClickhouseUser(e.currentTarget.value)} />
                  <Input
                    label="Contraseña"
                    type="password"
                    placeholder={config()?.clickhousePassword?.includes('***') ? '•••••••• (vacío = conservar)' : 'Vacía si no tiene'}
                    value={clickhousePassword()}
                    onInput={e => setClickhousePassword(e.currentTarget.value)}
                  />
                </div>

                <Input
                  label="Base de datos"
                  placeholder="audit"
                  helperText="Nombre de la BD en ClickHouse (por defecto: audit)"
                  value={clickhouseDb()}
                  onInput={e => setClickhouseDb(e.currentTarget.value)}
                />
              </div>

              <Show when={config()?.companyEndpointConfigured}>
                <Alert variant="success">Fan-out activo: esta empresa replica además hacia su propio ClickHouse.</Alert>
              </Show>

              <div class="flex justify-end">
                <Button color="primary" onClick={handleSave} disabled={saving()} prependIcon="material-symbols-light:save-outline">
                  {saving() ? 'Guardando...' : 'Guardar auditoría'}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <p class="text-xs text-foreground-subtle">
              Config efectiva: <span class="font-mono">{effectiveHint()}</span> · Lote {config()?.batchSize} · Reintentos {config()?.maxAttempts}
              <Show when={!config()?.companyEndpointConfigured}> · (del sistema)</Show>
            </p>
          </Card.Body>
        </Card>
      </Show>
    </div>
  )
}

export default TabAuditoria
